import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { ActionLogInfo, OrganizationInfo } from "@services/admin/types";
import { apiClient } from "../utils/apiClient";

interface AdminModalState {
  actionLogDetailsModal: {
    open: boolean;
    log: ActionLogInfo | null;
  };
  errorDetailsModal: {
    open: boolean;
    log: ActionLogInfo | null;
  };
  databaseObjectModal: {
    open: boolean;
    table: string | null;
    id: string | null;
  };
  emailModal: {
    open: boolean;
    emailId: string | null;
  };
  shipmentModal: {
    open: boolean;
    shipmentId: string | null;
  };
  resultDrawer: {
    open: boolean;
    resultId: string | null;
    input: { emailId?: string; shipmentId?: string; reminderId?: string; cutOffDate?: string } | null;
    resultIndex: number | null;
  };
  actionModal: {
    open: boolean;
    actionId: string | null;
  };
}

interface AdminStore {
  modals: AdminModalState;
  currentViewingIds: {
    gptRequestId: string | null;
    extractedDataId: string | null;
    derivedDataId: string | null;
    emailId: string | null;
  };
  organizations: {
    list: OrganizationInfo[];
    loading: boolean;
    error: string | null;
  };
}

export const initialState: AdminStore = {
  modals: {
    actionLogDetailsModal: {
      open: false,
      log: null,
    },
    errorDetailsModal: {
      open: false,
      log: null,
    },
    databaseObjectModal: {
      open: false,
      table: null,
      id: null,
    },
    emailModal: {
      open: false,
      emailId: null,
    },
    shipmentModal: {
      open: false,
      shipmentId: null,
    },
    resultDrawer: {
      open: false,
      resultId: null,
      input: null,
      resultIndex: null,
    },
    actionModal: {
      open: false,
      actionId: null,
    },
  },
  currentViewingIds: {
    gptRequestId: null,
    extractedDataId: null,
    derivedDataId: null,
    emailId: null,
  },
  organizations: {
    list: [],
    loading: false,
    error: null,
  },
};

// Async thunk to fetch organizations
export const fetchOrganizations = createAsyncThunk<OrganizationInfo[]>(
  "admin/fetchOrganizations",
  async () => {
    const response = await apiClient.post<{ organizations: OrganizationInfo[] }>(
      "/admin/organizations/list",
      {}
    );
    return response.organizations;
  }
);

const slice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    // Action Log Details Modal
    openActionLogDetailsModal: (state, { payload }: PayloadAction<ActionLogInfo>) => {
      state.modals.actionLogDetailsModal.open = true;
      state.modals.actionLogDetailsModal.log = payload;
    },
    closeActionLogDetailsModal: (state) => {
      state.modals.actionLogDetailsModal.open = false;
      state.modals.actionLogDetailsModal.log = null;
    },

    // Error Details Modal
    openErrorDetailsModal: (state, { payload }: PayloadAction<ActionLogInfo>) => {
      state.modals.errorDetailsModal.open = true;
      state.modals.errorDetailsModal.log = payload;
    },
    closeErrorDetailsModal: (state) => {
      state.modals.errorDetailsModal.open = false;
      state.modals.errorDetailsModal.log = null;
    },

    // Database Object Modal
    openDatabaseObjectModal: (state, { payload }: PayloadAction<{ table: string; id: string }>) => {
      state.modals.databaseObjectModal.open = true;
      state.modals.databaseObjectModal.table = payload.table;
      state.modals.databaseObjectModal.id = payload.id;
    },
    closeDatabaseObjectModal: (state) => {
      state.modals.databaseObjectModal.open = false;
      state.modals.databaseObjectModal.table = null;
      state.modals.databaseObjectModal.id = null;
    },

    // Email Modal
    openEmailModal: (state, { payload }: PayloadAction<string>) => {
      state.modals.emailModal.open = true;
      state.modals.emailModal.emailId = payload;
    },
    closeEmailModal: (state) => {
      state.modals.emailModal.open = false;
      state.modals.emailModal.emailId = null;
    },

    // Shipment Modal
    openShipmentModal: (state, { payload }: PayloadAction<string>) => {
      state.modals.shipmentModal.open = true;
      state.modals.shipmentModal.shipmentId = payload;
    },
    closeShipmentModal: (state) => {
      state.modals.shipmentModal.open = false;
      state.modals.shipmentModal.shipmentId = null;
    },

    // Result Drawer
    openResultDrawer: (state, { payload }: PayloadAction<{ resultId: string; input?: { emailId?: string; shipmentId?: string; reminderId?: string; cutOffDate?: string }; resultIndex?: number }>) => {
      state.modals.resultDrawer.open = true;
      state.modals.resultDrawer.resultId = payload.resultId;
      state.modals.resultDrawer.input = payload.input ?? null;
      state.modals.resultDrawer.resultIndex = payload.resultIndex ?? null;
    },
    closeResultDrawer: (state) => {
      state.modals.resultDrawer.open = false;
      state.modals.resultDrawer.resultId = null;
      state.modals.resultDrawer.input = null;
      state.modals.resultDrawer.resultIndex = null;
    },

    // Action Modal
    openActionModal: (state, { payload }: PayloadAction<string>) => {
      state.modals.actionModal.open = true;
      state.modals.actionModal.actionId = payload;
    },
    closeActionModal: (state) => {
      state.modals.actionModal.open = false;
      state.modals.actionModal.actionId = null;
    },

    // Navigation actions (for tracking current viewing state)
    setCurrentGptRequestId: (state, { payload }: PayloadAction<string | null>) => {
      state.currentViewingIds.gptRequestId = payload;
    },
    setCurrentExtractedDataId: (state, { payload }: PayloadAction<string | null>) => {
      state.currentViewingIds.extractedDataId = payload;
    },
    setCurrentDerivedDataId: (state, { payload }: PayloadAction<string | null>) => {
      state.currentViewingIds.derivedDataId = payload;
    },
    setCurrentEmailId: (state, { payload }: PayloadAction<string | null>) => {
      state.currentViewingIds.emailId = payload;
    },

    // Utility actions
    clearAllModals: (state) => {
      state.modals.actionLogDetailsModal.open = false;
      state.modals.actionLogDetailsModal.log = null;
      state.modals.errorDetailsModal.open = false;
      state.modals.errorDetailsModal.log = null;
      state.modals.databaseObjectModal.open = false;
      state.modals.databaseObjectModal.table = null;
      state.modals.databaseObjectModal.id = null;
      state.modals.emailModal.open = false;
      state.modals.emailModal.emailId = null;
      state.modals.shipmentModal.open = false;
      state.modals.shipmentModal.shipmentId = null;
      state.modals.resultDrawer.open = false;
      state.modals.resultDrawer.resultId = null;
      state.modals.resultDrawer.input = null;
      state.modals.actionModal.open = false;
      state.modals.actionModal.actionId = null;
    },
    clearCurrentViewingIds: (state) => {
      state.currentViewingIds.gptRequestId = null;
      state.currentViewingIds.extractedDataId = null;
      state.currentViewingIds.derivedDataId = null;
      state.currentViewingIds.emailId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizations.pending, (state) => {
        state.organizations.loading = true;
        state.organizations.error = null;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.organizations.loading = false;
        state.organizations.list = action.payload;
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.organizations.loading = false;
        state.organizations.error = action.error.message || "Failed to fetch organizations";
      });
  },
});

export const {
  openActionLogDetailsModal,
  closeActionLogDetailsModal,
  openErrorDetailsModal,
  closeErrorDetailsModal,
  openDatabaseObjectModal,
  closeDatabaseObjectModal,
  openEmailModal,
  closeEmailModal,
  openShipmentModal,
  closeShipmentModal,
  openResultDrawer,
  closeResultDrawer,
  openActionModal,
  closeActionModal,
  setCurrentGptRequestId,
  setCurrentExtractedDataId,
  setCurrentDerivedDataId,
  setCurrentEmailId,
  clearAllModals,
  clearCurrentViewingIds,
} = slice.actions;

// Selectors
export const selectActionLogDetailsModal = (state: { admin: AdminStore }) =>
  state.admin.modals.actionLogDetailsModal;

export const selectErrorDetailsModal = (state: { admin: AdminStore }) =>
  state.admin.modals.errorDetailsModal;

export const selectDatabaseObjectModal = (state: { admin: AdminStore }) =>
  state.admin.modals.databaseObjectModal;

export const selectEmailModal = (state: { admin: AdminStore }) =>
  state.admin.modals.emailModal;

export const selectShipmentModal = (state: { admin: AdminStore }) =>
  state.admin.modals.shipmentModal;

export const selectResultDrawer = (state: { admin: AdminStore }) =>
  state.admin.modals.resultDrawer;

export const selectActionModal = (state: { admin: AdminStore }) =>
  state.admin.modals.actionModal;

export const selectCurrentViewingIds = (state: { admin: AdminStore }) =>
  state.admin.currentViewingIds;

export const selectCurrentGptRequestId = (state: { admin: AdminStore }) =>
  state.admin.currentViewingIds.gptRequestId;

export const selectCurrentExtractedDataId = (state: { admin: AdminStore }) =>
  state.admin.currentViewingIds.extractedDataId;

export const selectCurrentDerivedDataId = (state: { admin: AdminStore }) =>
  state.admin.currentViewingIds.derivedDataId;

export const selectCurrentEmailId = (state: { admin: AdminStore }) =>
  state.admin.currentViewingIds.emailId;

export const selectOrganizations = (state: { admin: AdminStore }) =>
  state.admin.organizations;

export default slice.reducer;